import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, HOME_FOR_ROLE } from '../context/AuthContext';
import BrandMark from './BrandMark';
import LanguageSwitcher from './LanguageSwitcher';

export default function SiteLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const NAV = [
    { to: '/', label: t('nav.home'), end: true },
    { to: '/for-families', label: t('nav.forFamilies') },
    { to: '/for-nannies', label: t('nav.forNannies') },
    { to: '/jobs', label: t('nav.jobs') },
    { to: '/locations', label: t('nav.locations') },
    { to: '/about', label: t('nav.about') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/contact', label: t('nav.contact') },
  ];

  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Stop the page behind the mobile menu from scrolling.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <div className="site">
      <a className="skip-link" href="#main">{t('nav.skipToContent')}</a>

      <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="site-header-inner">
          <Link to="/" className="site-brand">
            <BrandMark />
            <span className="brand-words">
              <strong>Nannies to the Rescue</strong>
              <small>{t('nav.tagline')}</small>
            </span>
          </Link>

          <nav className="site-nav" aria-label="Main">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => (isActive ? 'is-active' : '')}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="site-actions">
            <LanguageSwitcher />
            {user ? (
              <Link className="btn btn-primary" to={HOME_FOR_ROLE[user.role] || '/'}>{t('nav.myPortal')}</Link>
            ) : (
              <>
                <Link className="btn btn-quiet" to="/login">{t('nav.login')}</Link>
                <Link className="btn btn-primary" to="/register/family">{t('nav.requestNanny')}</Link>
              </>
            )}
          </div>

          <button
            type="button"
            className={`burger ${menuOpen ? 'is-open' : ''}`}
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span /><span /><span />
          </button>
        </div>

        <div className={`mobile-menu ${menuOpen ? 'is-open' : ''}`} hidden={!menuOpen}>
          <nav aria-label="Mobile">
            {NAV.map((item, i) => (
              <NavLink key={item.to} to={item.to} end={item.end} style={{ transitionDelay: `${60 + i * 40}ms` }}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mobile-menu-actions">
            <LanguageSwitcher className="lang-switcher-mobile" />
            {user ? (
              <Link className="btn btn-primary btn-block" to={HOME_FOR_ROLE[user.role] || '/'}>{t('nav.myPortal')}</Link>
            ) : (
              <>
                <Link className="btn btn-secondary btn-block" to="/login">{t('nav.login')}</Link>
                <Link className="btn btn-primary btn-block" to="/register/family">{t('nav.requestNanny')}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="main">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="footer-brand">
            <BrandMark />
            <p className="footer-tagline">
              {t('footer.tagline')}
              <span className="footer-verse">{t('footer.verse')}</span>
            </p>
            <p className="footer-vetted">
              <strong>{t('footer.vettedTitle')}</strong> {t('footer.vetted')}
            </p>
          </div>
          <div className="footer-links">
            <h3>{t('footer.familiesHeading')}</h3>
            <Link to="/for-families">{t('footer.howItWorks')}</Link>
            <Link to="/register/family">{t('footer.createAccount')}</Link>
            <Link to="/login">{t('footer.familyLogin')}</Link>
          </div>
          <div className="footer-links">
            <h3>{t('footer.nanniesHeading')}</h3>
            <Link to="/for-nannies">{t('footer.workingWithUs')}</Link>
            <Link to="/jobs">{t('footer.jobBoard')}</Link>
            <Link to="/register/nanny">{t('footer.applyNow')}</Link>
            <Link to="/login">{t('footer.nannyLogin')}</Link>
          </div>
          <div className="footer-links">
            <h3>{t('footer.companyHeading')}</h3>
            <Link to="/about">{t('footer.aboutUs')}</Link>
            <Link to="/locations">{t('nav.locations')}</Link>
            <Link to="/faq">{t('nav.faq')}</Link>
            <Link to="/contact">{t('nav.contact')}</Link>
          </div>
        </div>
        <div className="site-footer-bar">
          <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
          <span>
            {t('footer.madeWith')}
            <svg className="footer-heart" viewBox="0 0 24 24" width="14" height="14" role="img" aria-label="love" fill="currentColor">
              <path d="M12 21s-7.5-4.6-9.6-9.3C.9 8.3 2.7 4.5 6.3 4.5c2 0 3.6 1.1 4.5 2.7h2.4c.9-1.6 2.5-2.7 4.5-2.7 3.6 0 5.4 3.8 3.9 7.2C19.5 16.4 12 21 12 21z" />
            </svg>
            {t('footer.madeWithSuffix')}
          </span>
        </div>
      </footer>
    </div>
  );
}
